import { LoaderFunction, useLoaderData } from "react-router-typesafe";
import {
  Model,
  PageModel,
  SurveyModel,
  ValueChangedEvent,
  DynamicPanelRemovingEvent,
  UpdateQuestionCssClassesEvent,
  LoadChoicesFromServerEvent,
  MatrixRowRemovingEvent,
  QuestionDropdownModel,
  ValueChangingEvent,
  CurrentPageChangingEvent,
  CompletingEvent,
  QuestionExpressionModel,
  CurrentPageChangedEvent,
  PanelModel,
  MatrixCellValueChangingEvent,
  DynamicPanelValueChangingEvent,
  Question,
  Serializer,
} from "survey-core";
import { useState, useCallback, useEffect, useRef } from "react";
import { T } from "@tolgee/react";
import { toast } from "react-toastify";
import { useHydrateAtoms } from "jotai/utils";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { AxiosResponse } from "axios";
import {
  isNewQuestionnaireAtom,
  languageQuestionAtom,
  explainerContentQuestionAtom,
  questionnaireNameAtom,
  uploadedFilesSetAtom,
  sidebarContentAtom,
  facilityDataAtom,
  completeTriesAtom,
  productPageVisitCountAtom,
  showValidationPopupAtom,
  validationPopupPropertiesAtom,
  crpQuestionnaireUrlAtom,
} from "../jotai-atoms";
import "survey-core/survey-core.css";
import "../components/survey";
import "../register-icons";
import "../override-locale";
import "../register-properties";
import "../validators";
import "../functions";
import "../component-collection";
import { CheckCircle, XCircle } from "react-feather";
import {
  buildDynamicPanelAnswerValue,
  compareFilteredChanges,
  getAllVisibleQuestionValueNames,
  getPanelIndexInsideDynamicPanel,
  getSideBarContent,
  isBoolean,
  isCheckbox,
  isCompositeChildValueChange,
  isDisplayMode,
  isDropdown,
  isDynamicPanel,
  isExpression,
  isFile,
  isMatrixDropdown,
  isMatrixDynamic,
  isPageTitle,
  isRemovingRowFromDynamicPanelOrMatrix,
  isSubTypeSourceChange,
  prepareQuestions,
  prepareSurvey,
  removeKeysFromArray,
  removeKeysFromObject,
  replaceChoicesUrlParamValuesWithAnswers,
  setSurveyInitialData,
  syncSubTypePanels,
  updateQueryStringValueWithoutNavigation,
} from "../utils";
import { ResourceType } from "../interfaces";
import {
  MenuDrawerTemplate,
  SurveyPageTemlpate,
  ValidationPopup,
  CheckEmailDialog,
} from "../components/templates";
import { ApiService } from "../services";
import { customCss } from "../survey-css";
import { themeJson } from "../theme";
import { ApiServiceProvider } from "../components/providers";
import { SidebarContent } from "../types";
import {
  registerDialogElement,
  registerDialogSerializer,
  registerCtaPanelElement,
  registerCtaPanelSerializer,
  registerCalendar,
} from "../models";

registerDialogElement();
registerDialogSerializer();
registerCtaPanelElement();
registerCtaPanelSerializer();
registerCalendar();

Serializer.findProperty("dropdown", "renderAs").defaultValue = "select";
Serializer.findProperty("tagbox", "renderAs").defaultValue = "multiselect";

export const surveyPageLoader = (async ({ params, request }: any) => {
  let sidebarContent: SidebarContent = { navigationItems: [] };
  const { gatherProcessId } = params;
  const apiService = new ApiService({
    gatherProcessId,
  });
  await apiService.fetchResourceType();
  const { data: schema } = await apiService.fetchSchema();
  const questionnaireIdKey = sessionStorage.getItem("questionnaire-id-key");
  const searchParams = new URL(request.url).searchParams;
  let isReadonlyView = Array.from(searchParams.entries()).some(
    ([key, value]) => key.toLowerCase() === "readonly" && value === "true"
  );
  let questionnaireId = searchParams.get("ques_id");

  if (questionnaireId) {
    apiService.questionnaireId = questionnaireId;
  } else if (schema.questionnaireId) {
    apiService.questionnaireId = schema.questionnaireId;
  }

  if (questionnaireIdKey && !apiService.questionnaireId) {
    apiService.questionnaireId = questionnaireIdKey;
  }

  const survey = new Model();
  survey.applyTheme(themeJson);
  survey.css = customCss;
  survey.navigateToUrl = "/complete";
  survey.fromJSON(schema);
  survey.showPreviewBeforeComplete = "showAllQuestions";
  try {
    const { data } = await apiService.getSideBarContent();
    sidebarContent = data.navigationItems.length
      ? data
      : getSideBarContent(survey, apiService.resourceType);
  } catch (e) {
    sidebarContent = getSideBarContent(survey, apiService.resourceType);
  }
  prepareSurvey(survey);
  prepareQuestions(survey.getAllQuestions());
  const explainerPage = survey.getPageByName("explainer-page");
  let contentQuestion;
  if (explainerPage) {
    contentQuestion = explainerPage.questions.find((q) =>
      q.name.includes("explainer-page-content")
    );
  }
  if (apiService.questionnaireId !== "") {
    survey.removePage(explainerPage);
    const { data: answers } = await apiService.fetchAnswers();

    setSurveyInitialData(survey, answers);
    const { pageNumber } = answers;
    if (pageNumber && pageNumber > -1) {
      survey.currentPageNo = pageNumber;
    }

    if (isReadonlyView) {
      replaceChoicesUrlParamValuesWithAnswers(
        answers,
        survey.getAllQuestions()
      );
      survey.readOnly = true;
      survey.questionsOnPageMode = "singlePage";
    }

    if (!isReadonlyView) {
      survey.start();
    }

    return {
      survey,
      gatherProcessId,
      answers,
      apiService,
      contentQuestion,
      sidebarContent,
    };
  } else {
    let answers = null;
    if (apiService.resourceType === "product-questionnaire") {
      const { data } = await apiService.fetchAnswers();
      answers = data;
      setSurveyInitialData(survey, answers);
    }
    return {
      survey,
      gatherProcessId,
      answers,
      apiService,
      contentQuestion,
      sidebarContent,
    };
  }
}) satisfies LoaderFunction;

export const SurveyPage = () => {
  const { survey, apiService, contentQuestion, sidebarContent } =
    useLoaderData<typeof surveyPageLoader>();
  const pendingQuestionsRef = useRef(new Map<string, any>()); //this is used to store the pending to be saved questions that are not yet saved and their last known good values
  // TODO: @Amit this is commented because it is not used. please lets check if this is needed at all so I can remove it from this branch
  // const menuItems = getSideBarItems(survey, apiService.resourceType);
  useHydrateAtoms([
    [crpQuestionnaireUrlAtom, survey.getPropertyValue("crpQuestionnaireUrl")],
    [isNewQuestionnaireAtom, survey.isShowStartingPage],
    [questionnaireNameAtom, survey.title],
    [languageQuestionAtom, getLanguageDropdownQuestion(survey)],
    [explainerContentQuestionAtom, contentQuestion as QuestionExpressionModel],
    [sidebarContentAtom, sidebarContent],
    [
      productPageVisitCountAtom,
      survey.currentPage.getPropertyValue("subtype", "") === "productPage"
        ? 1
        : 0,
    ],
    [
      facilityDataAtom,
      {
        facilityShortName: survey.getPropertyValue("facilityShortName"),
        brandName: survey.getPropertyValue("brandName"),
      },
    ],
  ]);
  const [showCheckEmailDialog, setShowCheckEmailDialog] = useState(false);
  const [userEmailAddress, setUserEmailAddress] = useState("");
  const setProductPageVisitCount = useSetAtom(productPageVisitCountAtom);
  const [completeTries, setCompleteTries] = useAtom(completeTriesAtom);
  const uploadedFilesSet = useAtomValue(uploadedFilesSetAtom);
  const [activePage, setActivePage] = useState<PageModel>(survey.activePage);
  const [showSideBar, setShowSideBar] = useState(
    !survey.isStartPageActive &&
      survey.state !== "completed" &&
      survey.state !== "preview" &&
      !isDisplayMode(survey)
  );
  const [isNewQuestionnaire, setIsNewQuestionnaire] = useAtom(
    isNewQuestionnaireAtom
  );

  const [showNavButtons, setShowNavButton] = useState(true);
  const [showValidationPopup, setShowValidationPopup] = useAtom(
    showValidationPopupAtom
  );
  const validationPopupProperties = useAtomValue(validationPopupPropertiesAtom);

  const canPerformChanges = useCallback(
    () => survey.state === "running" && !isDisplayMode(survey),
    [survey]
  );

  const setSurveyCurrentPage = useCallback(
    (name: string) => {
      survey.currentPage = survey.getPageByName(name);
    },
    [survey]
  );

  const onStarted = useCallback((sender: Model) => {
    sender.pageNextText = "%-%tolgee:button-begin-questionnaire%-%";
    setShowSideBar(false);
    setActivePage(sender.activePage);
  }, []);

  useEffect(() => {
    const onValueChanging = (
      _: SurveyModel,
      { question, value, oldValue, name }: ValueChangingEvent
    ) => {
      let rawValue: any;

      const shouldSkipMatrix =
        isMatrixDropdown(question) && // dynamicMatrix has its own handler
        apiService.backendVersion > 1; // the handler is only support by backend version > 1

      // dynamic panels are handled by dynamicPanelValueChanging - except for a
      // composite child, which that event never sees, so this is the only place
      // its value can be saved.
      const shouldSkipDynamicPanel =
        isDynamicPanel(question) &&
        !isCompositeChildValueChange(question, oldValue, value);

      if (
        !canPerformChanges() ||
        !apiService.questionnaireId ||
        isFile(question) ||
        shouldSkipMatrix ||
        shouldSkipDynamicPanel ||
        isRemovingRowFromDynamicPanelOrMatrix(question, oldValue, value) ||
        !question
      ) {
        return;
      }

      if (isDynamicPanel(question)) {
        const result = compareFilteredChanges(
          value,
          oldValue,
          uploadedFilesSet,
          removeKeysFromArray
        );
        if (!result) return;
      }

      if (isMatrixDynamic(question) || isMatrixDropdown(question)) {
        const result = compareFilteredChanges(
          value,
          oldValue,
          uploadedFilesSet,
          removeKeysFromObject
        );
        if (!result) return;
      }

      updatePendingQuestionValue(pendingQuestionsRef, name, value, oldValue);

      if (isDropdown(question) || isBoolean(question)) {
        rawValue = pendingQuestionsRef.current.get(name).pendingValue ?? "";
      } else {
        const pendingValue =
          pendingQuestionsRef.current?.get(name)?.pendingValue;
        const pendingValueIsNotDefined =
          pendingValue === undefined || pendingValue === null;

        rawValue = pendingValueIsNotDefined
          ? pendingValue
          : JSON.parse(JSON.stringify(pendingValue));
      }

      apiService
        .saveAnswer(name, { value: rawValue })
        .then(() => {
          //if the save is successful, delete the pending value
          pendingQuestionsRef.current.delete(name);
        })
        .catch((e) => {
          //if the save fails, restore the last known good value
          const { lastKnownGoodValue } =
            pendingQuestionsRef.current.get(name) || {};
          question.value = lastKnownGoodValue;
          pendingQuestionsRef.current.delete(name);
          toast.error(() => <T keyName="save-answer-fail-error-message" />, {
            icon: <XCircle color="var(--error-500)" />,
          });
        });
    };
    survey.onValueChanging.add(onValueChanging);
    return () => {
      survey.onValueChanging.remove(onValueChanging);
    };
  }, [apiService, canPerformChanges, survey, uploadedFilesSet]);

  const onShowingPreview = useCallback(() => {
    setShowSideBar(false);
  }, []);

  const onUpdateQuestionCssClasses = useCallback(
    (
      _: SurveyModel,
      { cssClasses, question }: UpdateQuestionCssClassesEvent
    ) => {
      if (isExpression(question)) {
        if (isPageTitle(question)) cssClasses.mainRoot += " !px-0"; //set x-axis padding to none
      } else if (isCheckbox(question)) {
        cssClasses.title += " !text-sm"; //force big font size (even if on mobile)
      }
    },
    []
  );

  useEffect(() => {
    const onDynamicPanelValueChanging = (
      sender: SurveyModel,
      {
        question,
        value,
        oldValue,
        name,
        panelIndex,
        panel,
      }: DynamicPanelValueChangingEvent
    ) => {
      // Resolve the nested question from the panel by NAME. The event `name`
      // is the child's element name, so getQuestionByValueName(name) misses
      // whenever the backend injected a distinct valueName.
      const updatedNestedQuestion = panel.getQuestionByName(name);
      const dpQuestionName = question.getValueName();

      /**
       * matrix dropdown has its own handler but it is only supported by backend when version is above 1
       */
      const shouldSkipMatrix = (updatedNestedQuestion: Question) =>
        isMatrixDropdown(updatedNestedQuestion) &&
        apiService.backendVersion > 1;

      const shouldSkip =
        updatedNestedQuestion &&
        (shouldSkipMatrix(updatedNestedQuestion) || // matrixDropdown has its own handler
          isFile(updatedNestedQuestion)); // file uses the upload API

      if (shouldSkip) {
        return;
      }

      updatePendingQuestionValue(pendingQuestionsRef, name, value, oldValue);

      const rawValue = buildDynamicPanelAnswerValue(
        question,
        panel,
        panelIndex,
        name,
        value
      );

      apiService
        .saveAnswer(dpQuestionName, { value: rawValue })
        .then(() => {
          //if the save is successful, delete the pending value
          pendingQuestionsRef.current.delete(name);
        })
        .catch((e) => {
          //if the save fails, restore the last known good value
          const { lastKnownGoodValue } =
            pendingQuestionsRef.current.get(name) || {};
          question.value = lastKnownGoodValue;
          pendingQuestionsRef.current.delete(name);
          toast.error(() => <T keyName="save-answer-fail-error-message" />, {
            icon: <XCircle color="var(--error-500)" />,
          });
        });
    };
    survey.onDynamicPanelValueChanging.add(onDynamicPanelValueChanging);
    return () => {
      survey.onDynamicPanelValueChanging.remove(onDynamicPanelValueChanging);
    };
  }, [apiService, survey]);

  useEffect(() => {
    /**
     * An event handler for the matrix cell value changing event.
     * @param _ - The survey model
     * @param {MatrixCellValueChangingEvent} options - The event object
     * @returns {void}
     */
    const onMatrixCellValueChanging = (
      _: SurveyModel,
      {
        question,
        row,
        cellQuestion,
        columnName,
        value,
        oldValue,
      }: MatrixCellValueChangingEvent
    ) => {
      const shouldSkip =
        !isMatrixDropdown(question) || // only matrixDropdown cell change have the backend support
        isFile(cellQuestion) || // file questions uses the upload API
        apiService.isProductResourceType || // matrixDropdown cell change is not support by PQ backend
        apiService.backendVersion < 2; // backend version < 2 does not support matrixDropdown cell change

      if (shouldSkip) {
        return;
      }

      const matrixValue = question.value;
      const questionName = question.valueName;
      const rowName = row.rowName;
      const parent = question.parent;
      let parentValueName = undefined;
      let parentGroupIndex = undefined;
      if (parent?.isPanel) {
        const parentPanel = parent as PanelModel;
        if (isDynamicPanel(parentPanel.parentQuestion)) {
          parentGroupIndex = getPanelIndexInsideDynamicPanel(question);
          parentValueName = parentPanel.parentQuestion.getValueName();
        }
      }

      const fullValue = {
        value,
        matrixValue,
        rowName,
        columnName,
        parentGroupIndex,
        parentValueName,
      };

      //update the pending question value
      updatePendingQuestionValue(
        pendingQuestionsRef,
        cellQuestion.getValueName(),
        value,
        oldValue
      );

      apiService
        .saveAnswer(questionName, fullValue)
        .then(() => {
          //if the save is successful, delete the pending value
          pendingQuestionsRef.current.delete(cellQuestion.getValueName());
        })
        .catch((e) => {
          //if the save fails, restore the last known good value
          const { lastKnownGoodValue } =
            pendingQuestionsRef.current.get(cellQuestion.getValueName()) || {};
          question.value = lastKnownGoodValue;
          pendingQuestionsRef.current.delete(cellQuestion.getValueName());
          toast.error(() => <T keyName="save-answer-fail-error-message" />, {
            icon: <XCircle color="var(--error-500)" />,
          });
        });
    };
    survey.onMatrixCellValueChanging.add(onMatrixCellValueChanging);
    return () => {
      survey.onMatrixCellValueChanging.remove(onMatrixCellValueChanging);
    };
  }, [apiService, survey]);

  const onLoadChoicesFromServer = useCallback(
    (_: SurveyModel, { question, choices }: LoadChoicesFromServerEvent) => {
      if (question.name.includes("facility-state-province")) {
        question.showNoneItem = choices.length < 1;
      }
    },
    []
  );

  useEffect(() => {
    const allowDynamicPanelRemovingMap = new Map<
      string,
      Map<number, boolean>
    >();

    const onDynamicPanelRemoving = (
      _: SurveyModel,
      options: DynamicPanelRemovingEvent
    ) => {
      if (
        !canPerformChanges() ||
        options.question.minPanelCount === options.question.panels.length
      ) {
        return;
      }
      const sectionValueName = options.question.getValueName();

      let questionFlagsMap = allowDynamicPanelRemovingMap.get(sectionValueName);
      let allowRemoving = false;

      if (questionFlagsMap) {
        const tmp = questionFlagsMap.get(options.panelIndex);
        if (tmp) allowRemoving = tmp;
        else {
          questionFlagsMap.set(options.panelIndex, allowRemoving);
        }
      } else {
        questionFlagsMap = new Map();
        questionFlagsMap.set(options.panelIndex, false);
        allowDynamicPanelRemovingMap.set(sectionValueName, questionFlagsMap);
      }

      options.allow = allowRemoving;

      if (!allowRemoving) {
        removingDynamicPanelToast(
          apiService.removeGroupByIndex(sectionValueName, options.panelIndex)
        )
          .then(() => {
            questionFlagsMap?.set(options.panelIndex, true);
            options.question.removePanel(options.panelIndex);
          })
          .catch();
      } else {
        //reset the flag
        questionFlagsMap?.set(options.panelIndex, false);
      }
    };
    survey.onDynamicPanelRemoving.add(onDynamicPanelRemoving);
    return () => {
      survey.onDynamicPanelRemoving.remove(onDynamicPanelRemoving);
    };
  }, [survey, canPerformChanges, apiService]);

  useEffect(() => {
    const allowRowRemovingMap = new Map<string, Map<number, boolean>>();
    const onMatrixRowRemoving = (_: Model, options: MatrixRowRemovingEvent) => {
      if (!canPerformChanges() || !options.question.canRemoveRows) {
        return;
      }

      if (!options.question.getPropertyValue("isRemoteRemoveAllowed")) {
        return;
      }

      const sectionValueName = options.question.getValueName();

      let questionFlagsMap = allowRowRemovingMap.get(sectionValueName);
      let allowRemoving = false;

      if (questionFlagsMap) {
        const tmp = questionFlagsMap.get(options.rowIndex);
        if (tmp) allowRemoving = tmp;
        else {
          questionFlagsMap.set(options.rowIndex, allowRemoving);
        }
      } else {
        questionFlagsMap = new Map();
        questionFlagsMap.set(options.rowIndex, false);
        allowRowRemovingMap.set(sectionValueName, questionFlagsMap);
      }

      options.allow = allowRemoving;

      if (!allowRemoving) {
        removingMatrixRowToast(
          apiService.removeGroupByIndex(sectionValueName, options.rowIndex)
        )
          .then(() => {
            questionFlagsMap?.set(options.rowIndex, true);
            options.question.removeRow(options.rowIndex);
          })
          .catch();
      } else {
        //reset the flag
        questionFlagsMap?.set(options.rowIndex, false);
      }
    };
    survey.onMatrixRowRemoving.add(onMatrixRowRemoving);
    return () => {
      survey.onMatrixRowRemoving.remove(onMatrixRowRemoving);
    };
  }, [apiService, canPerformChanges, survey]);

  useEffect(() => {
    survey.onLoadChoicesFromServer.add(onLoadChoicesFromServer);
    return () => survey.onLoadChoicesFromServer.remove(onLoadChoicesFromServer);
  }, [onLoadChoicesFromServer, survey]);

  useEffect(() => {
    survey.onUpdateQuestionCssClasses.add(onUpdateQuestionCssClasses);
    return () =>
      survey.onUpdateQuestionCssClasses.remove(onUpdateQuestionCssClasses);
  }, [onUpdateQuestionCssClasses, survey.onUpdateQuestionCssClasses]);

  useEffect(() => {
    survey.onShowingPreview.add(onShowingPreview);
    return () => {
      survey.onShowingPreview.remove(onShowingPreview);
    };
  }, [survey, onShowingPreview]);

  useEffect(() => {
    const onComplete = (sender: SurveyModel) => {
      setShowSideBar(false);
      setShowNavButton(false);
    };

    survey.onComplete.add(onComplete);
    return () => {
      survey.onComplete.remove(onComplete);
    };
  }, [survey]);

  const [isCompleting, setIsCompleting] = useState(false);

  useEffect(() => {
    const onCompleting = (sender: SurveyModel, options: CompletingEvent) => {
      if (isCompleting) {
        options.allow = true;
        return;
      }

      sender.currentPageNo = 0;
      sender.readOnly = true;
      setIsCompleting(true);
      setCompleteTries(completeTries + 1);
      options.allow = false; // Prevent immediate completion

      completeQuestionnaireToast(
        apiService.completeQuestionnaire(
          getAllVisibleQuestionValueNames(survey),
          sender.data
        )
      )
        .then(() => {
          sessionStorage.clear();
          setCompleteTries(0);
          setShowSideBar(false);
          setShowNavButton(false);
          sender.doComplete(); // This will trigger onCompleting again, but with isCompleting=true
        })
        .catch(() => {
          setShowSideBar(true);
          setShowNavButton(true);
          options.allow = false;
        })
        .finally(() => {
          sender.readOnly = false;
        });
    };
    survey.onCompleting.add(onCompleting);
    return () => {
      survey.onCompleting.remove(onCompleting);
    };
  }, [apiService, survey, completeTries, setCompleteTries, isCompleting]);

  useEffect(() => {
    survey.onStarted.add(onStarted);
    return () => {
      survey.onStarted.remove(onStarted);
    };
  }, [survey, onStarted]);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (apiService.pending) {
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [apiService.pending]);

  /**
   * make sure state-province options includes None everytime after country value is changing
   * and there are no pre-loaded option for state-province
   */
  useEffect(() => {
    const onValueChanged = (
      survey: SurveyModel,
      { question }: ValueChangedEvent
    ) => {
      if (isDropdown(question) && question.name.includes("facility-country")) {
        const stateQuestion = survey
          .getAllQuestions()
          .find((q) => q.name.includes("facility-state-province"));
        if (stateQuestion)
          (stateQuestion as QuestionDropdownModel).showNoneItem =
            (stateQuestion as QuestionDropdownModel).choices.length < 1;
      }
    };
    survey.onValueChanged.add(onValueChanged);
    return () => {
      survey.onValueChanged.remove(onValueChanged);
    };
  }, [survey.onValueChanged]);

  /**
   * Keeps sub-type-driven dynamic panels in step with the checkbox that drives
   * them - one production-output table per selected product sub-type, plus one
   * titled with the "Other" free text.
   *
   * Registered separately from the handler above, which is country/state
   * specific. syncSubTypePanels is idempotent, so the assignment it makes here
   * cannot re-trigger this handler into a loop.
   *
   * @see src/utils/sub-type-panels.ts
   */
  useEffect(() => {
    const onSubTypeSelectionChanged = (
      survey: SurveyModel,
      { name }: ValueChangedEvent
    ) => {
      if (!canPerformChanges() || !isSubTypeSourceChange(survey, name)) {
        return;
      }

      syncSubTypePanels(survey, {
        removeGroup: (valueName, panelIndex) => {
          if (!apiService.questionnaireId) return;
          // Assigning the panel value skips onDynamicPanelRemoving, so the
          // stored group has to be deleted here or it is orphaned server-side.
          apiService.removeGroupByIndex(valueName, panelIndex).catch(() => {
            // Best effort: the respondent has already seen the table go, and
            // the next child edit re-sends the whole panel array anyway.
          });
        },
      });
    };
    survey.onValueChanged.add(onSubTypeSelectionChanged);
    return () => {
      survey.onValueChanged.remove(onSubTypeSelectionChanged);
    };
  }, [survey.onValueChanged, apiService, canPerformChanges]);

  const saveLanguageOnNewQuestionnaire = useCallback(() => {
    const languageQuestion = getLanguageDropdownQuestion(survey);
    if (languageQuestion)
      apiService
        .saveAnswer(languageQuestion.valueName, {
          value: languageQuestion.value,
        })
        .catch((e) => {
          toast.error(() => <T keyName="save-answer-fail-error-message" />, {
            icon: <XCircle color="var(--error-500)" />,
          });
        });
  }, [apiService, survey]);

  useEffect(() => {
    let allowPageChange = false;
    const onCurrentPageChanging = (
      sender: SurveyModel,
      options: CurrentPageChangingEvent
    ) => {
      prepareQuestions(options.newCurrentPage.questions);
      if (options.oldCurrentPage?.name === "explainer-page") {
        options.allow = allowPageChange;
        if (!allowPageChange) {
          const showQuestionnaireCreatedToast = sender.getPropertyValue(
            "showQuestionnaireCreatedToast"
          );

          generateQuestionnaireIdToast(
            apiService.createQuestionnaire(sender.data),
            apiService.resourceType,
            showQuestionnaireCreatedToast
          )
            .then(async (res) => {
              updateQueryStringValueWithoutNavigation(
                "ques_id",
                res.data.questionnaireId
              );
              setShowCheckEmailDialog(
                survey.getPropertyValue("withCheckEmailDialog")
              );
              setUserEmailAddress(sender.getQuestionByValueName("email").value);
              sessionStorage.setItem(
                "questionnaire-id-key",
                res.data.questionnaireId
              );
              sender.pageNextText = "%-%tolgee:button-next%-%";
              setIsNewQuestionnaire(false);
              allowPageChange = true;
              saveLanguageOnNewQuestionnaire();
              // preload answers to the survey only for facility-questionnaire
              if (
                res.data.answers &&
                apiService.resourceType === "facility-questionnaire"
              ) {
                setSurveyInitialData(sender, res.data.answers);
              }
              survey.nextPage();
            })
            .catch();
        } else {
          allowPageChange = true;
        }
      } else {
        apiService.savePageNumber(options.newCurrentPage.num - 1).catch();
      }
    };

    survey.onCurrentPageChanging.add(onCurrentPageChanging);
    return () => {
      survey.onCurrentPageChanging.remove(onCurrentPageChanging);
    };
  }, [
    apiService,
    saveLanguageOnNewQuestionnaire,
    setIsNewQuestionnaire,
    survey,
  ]);

  useEffect(() => {
    const onCurrentPageChanged = (
      sender: SurveyModel,
      options: CurrentPageChangedEvent
    ) => {
      //update the page visit count
      const subtype = options.newCurrentPage.getPropertyValue("subtype", "");
      if (subtype === "productPage") {
        setProductPageVisitCount((prev) => prev + 1);
      }

      setShowSideBar(true);
      setActivePage(sender.activePage);
      sender.removePage(sender.getPageByName("explainer-page"));
    };
    survey.onCurrentPageChanged.add(onCurrentPageChanged);
    return () => {
      survey.onCurrentPageChanged.remove(onCurrentPageChanged);
    };
  }, [setProductPageVisitCount, survey]);

  return (
    <ApiServiceProvider apiService={apiService}>
      {showCheckEmailDialog &&
        apiService.resourceType === "facility-questionnaire" && (
          <CheckEmailDialog emailAddress={userEmailAddress} />
        )}
      <ValidationPopup
        open={showValidationPopup}
        setOpen={() => setShowValidationPopup(false)}
        title={validationPopupProperties.title || ""}
        contentMessage={validationPopupProperties.contentMessage || ""}
        errorMessage={validationPopupProperties.errorMessage || ""}
        buttonText={validationPopupProperties.buttonText || ""}
      />
      <MenuDrawerTemplate
        items={[]}
        disabled={!showSideBar}
        activePageName={activePage.name}
        onItemClick={setSurveyCurrentPage}
      />
      <SurveyPageTemlpate
        menuProps={{
          disabled: !apiService.questionnaireId,
          activePageName: activePage.name,
          onItemClick: setSurveyCurrentPage,
        }}
        survey={survey}
        isNewQuestionnaire={isNewQuestionnaire}
        showSideBar={
          survey.getPropertyValue("navigationMenu") === "sidebar" && showSideBar
        }
        showNavButtons={showNavButtons}
      />
    </ApiServiceProvider>
  );
};

const generateQuestionnaireIdToast = (
  promiseFunc: Promise<AxiosResponse>,
  resourceType: ResourceType,
  showQuestionnaireCreatedToast: boolean
) => {
  return toast.promise(promiseFunc, {
    pending: {
      render: () => <T keyName="generate-qid-pending" />,
    },
    success:
      resourceType === "facility-questionnaire" && showQuestionnaireCreatedToast
        ? {
            render: () => (
              <T
                keyName={
                  resourceType === "facility-questionnaire"
                    ? "dgq-generate-qid-success"
                    : "bpq-generate-qid-success"
                }
              />
            ),
            icon: <CheckCircle color="var(--success-500)" />,
            autoClose: false,
            //don't hide language dropdown with toast
            className: "top-[57px]",
          }
        : undefined,
    error: {
      render: () => <T keyName="generate-qid-fail" />,
      icon: <XCircle color="var(--error-500)" />,
      //don't hide language dropdown with toast
      className: "top-[57px]",
    },
  });
};

const completeQuestionnaireToast = (promiseFunc: Promise<AxiosResponse>) => {
  return toast.promise(promiseFunc, {
    pending: { render: () => <T keyName="complete-questionnaire-pending" /> },
    success: {
      render: () => <T keyName="complete-questionnaire-success" />,
      icon: <CheckCircle color="var(--success-500)" />,
    },
    error: {
      onClose: () => window.location.reload(),
      render: () => <T keyName="complete-questionnaire-fail" />,
      icon: <XCircle color="var(--error-500)" />,
    },
  });
};

const removingMatrixRowToast = (promiseFunc: Promise<AxiosResponse>) => {
  return toast.promise(promiseFunc, {
    error: {
      render: () => <T keyName="removing-row-error-message" />,
      icon: <XCircle color="var(--error-500)" />,
    },
  });
};

const removingDynamicPanelToast = (promiseFunc: Promise<AxiosResponse>) => {
  return toast.promise(promiseFunc, {
    error: {
      render: () => <T keyName="removing-panel-error-message" />,
      icon: <XCircle color="var(--error-500)" />,
    },
  });
};

function getLanguageDropdownQuestion(survey: SurveyModel) {
  const introPage = survey.startedPage;
  const languageQuestion = introPage.questions.find((q) =>
    q.name.includes("language")
  );
  return languageQuestion;
}

/**
 * Updates the pending question value in the pendingQuestionsRef map.
 * @param {React.MutableRefObject<Map<string, any>>} pendingQuestionsRef - The ref to the pending questions map
 * @param {string} name - The name of the question
 * @param {any} value - The new value of the question
 * @param {any} oldValue - The old value of the question
 */
const updatePendingQuestionValue = (
  pendingQuestionsRef: React.MutableRefObject<Map<string, any>>,
  name: string,
  value: any,
  oldValue: any
) => {
  //if a save is already pending, update the pending value
  if (pendingQuestionsRef.current.has(name)) {
    pendingQuestionsRef.current.set(name, {
      pendingValue: value,
      lastKnownGoodValue:
        pendingQuestionsRef.current.get(name).lastKnownGoodValue,
    });
  } else {
    //otherwise, set the pending value and the last known good value
    pendingQuestionsRef.current.set(name, {
      pendingValue: value,
      lastKnownGoodValue: oldValue,
    });
  }
};
